import { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { SxProps } from '@mui/material';

export const PROFESSIONAL_LIGHT_ACCENT = 'rgb(25, 25, 25)';
export const PROFESSIONAL_LIGHT_ACCENT_BORDER = 'rgba(25, 25, 25, 0.38)';
export const PROFESSIONAL_LIGHT_COLLAPSED_STRIP_BG = 'rgb(238, 238, 238)';
export const PROFESSIONAL_LIGHT_COLLAPSED_STRIP_HOVER_BG = 'rgb(220, 220, 220)';

export const isProfessionalLightTheme = (theme: Theme): boolean =>
    theme.palette.mode === 'light' && Boolean(theme.palette.dashboard?.heroTileForegroundColor);

/** Bordered toolbar IconButton — PROFESSIONAL light uses dark grey instead of remapped secondary. */
export const professionalLightBorderedIconButtonSx = (theme: Theme): SxProps<Theme> => {
    if (!isProfessionalLightTheme(theme)) {
        return {
            border: '1px solid',
            borderColor: 'secondary.main',
            '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'secondary.dark',
            },
        };
    }
    return {
        border: '1px solid',
        borderColor: PROFESSIONAL_LIGHT_ACCENT_BORDER,
        color: PROFESSIONAL_LIGHT_ACCENT,
        '& .MuiSvgIcon-root': { color: PROFESSIONAL_LIGHT_ACCENT },
        '&:hover': {
            backgroundColor: 'rgba(25, 25, 25, 0.06)',
            borderColor: PROFESSIONAL_LIGHT_ACCENT,
        },
    };
};

/** Grid/table view toggle — PROFESSIONAL light selected state uses dark grey fill. */
export const professionalLightViewToggleGroupSx = (theme: Theme): SxProps<Theme> => {
    const base = { flexShrink: 0 };
    if (!isProfessionalLightTheme(theme)) {
        return {
            ...base,
            '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText',
                    '&:hover': { backgroundColor: 'secondary.dark' },
                },
            },
        };
    }
    return {
        ...base,
        '& .MuiToggleButton-root': {
            border: '1px solid',
            borderColor: PROFESSIONAL_LIGHT_ACCENT_BORDER,
            color: PROFESSIONAL_LIGHT_ACCENT,
            '& .MuiSvgIcon-root': { color: PROFESSIONAL_LIGHT_ACCENT },
            '&:hover': {
                backgroundColor: 'rgba(25, 25, 25, 0.06)',
            },
            '&.Mui-selected': {
                backgroundColor: PROFESSIONAL_LIGHT_ACCENT,
                color: '#ffffff',
                borderColor: PROFESSIONAL_LIGHT_ACCENT,
                '& .MuiSvgIcon-root': { color: '#ffffff' },
                '&:hover': {
                    backgroundColor: 'rgb(15, 15, 15)',
                },
            },
        },
    };
};

/** Fullscreen asset carousel footer text — PROFESSIONAL light only (background unchanged). */
export const carouselFooterTitleSx = (theme: Theme): SxProps<Theme> =>
    isProfessionalLightTheme(theme) ? { color: PROFESSIONAL_LIGHT_ACCENT, fontWeight: 700 } : { color: '#fff', fontWeight: 700 };

export const carouselFooterCaptionSx = (theme: Theme): SxProps<Theme> =>
    isProfessionalLightTheme(theme) ? { color: PROFESSIONAL_LIGHT_ACCENT } : { color: 'rgba(255,255,255,0.65)' };

/** Carousel footer icon buttons (download / open / copy) — PROFESSIONAL light only. */
export const carouselFooterIconBtnSx = (theme: Theme): SxProps<Theme> => {
    if (!isProfessionalLightTheme(theme)) {
        return {
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.5)',
            '&:hover': {
                bgcolor: 'rgba(255,255,255,0.12)',
                borderColor: 'rgba(255,255,255,0.75)',
            },
        };
    }
    return {
        color: PROFESSIONAL_LIGHT_ACCENT,
        border: `1px solid ${PROFESSIONAL_LIGHT_ACCENT_BORDER}`,
        '& .MuiSvgIcon-root': { color: PROFESSIONAL_LIGHT_ACCENT },
        '&:hover': {
            bgcolor: 'rgba(25, 25, 25, 0.06)',
            borderColor: PROFESSIONAL_LIGHT_ACCENT,
        },
    };
};

const carouselPlayerAccent = (theme: Theme) =>
    isProfessionalLightTheme(theme) ? PROFESSIONAL_LIGHT_ACCENT : theme.palette.secondary.main;

/** Carousel video/audio player control bar — PROFESSIONAL light uses dark controls. */
export const carouselPlayerControlBtnSx = (theme: Theme): SxProps<Theme> => {
    const accent = carouselPlayerAccent(theme);
    return {
        color: accent,
        bgcolor: alpha(accent, 0.14),
        border: `1px solid ${alpha(accent, 0.45)}`,
        '& .MuiSvgIcon-root': { color: accent },
        '&:hover': {
            bgcolor: alpha(accent, 0.26),
            borderColor: accent,
        },
        '&.Mui-disabled': {
            color: alpha(accent, 0.35),
            borderColor: alpha(accent, 0.2),
            bgcolor: alpha(accent, 0.06),
            '& .MuiSvgIcon-root': { color: alpha(accent, 0.35) },
        },
    };
};

export const carouselPlayerSliderSx = (theme: Theme): SxProps<Theme> => {
    const accent = carouselPlayerAccent(theme);
    return {
        width: 120,
        color: accent,
        '& .MuiSlider-thumb': {
            width: 12,
            height: 12,
            backgroundColor: accent,
            '&:hover, &.Mui-focusVisible': {
                boxShadow: `0 0 0 8px ${alpha(accent, 0.22)}`,
            },
        },
        '& .MuiSlider-track': {
            backgroundColor: accent,
            border: 'none',
        },
        '& .MuiSlider-rail': {
            backgroundColor: alpha(accent, 0.28),
            opacity: 1,
        },
        '& .MuiSlider-valueLabel': {
            backgroundColor: accent,
            color: '#fff',
        },
    };
};

export const carouselPlayerControlBarSx = (theme: Theme): SxProps<Theme> => {
    const accent = carouselPlayerAccent(theme);
    return {
        flexWrap: 'wrap' as const,
        justifyContent: 'center' as const,
        alignItems: 'center' as const,
        gap: 1,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        bgcolor: isProfessionalLightTheme(theme)
            ? 'rgba(255, 255, 255, 0.92)'
            : alpha(accent, 0.08),
        border: `1px solid ${isProfessionalLightTheme(theme) ? PROFESSIONAL_LIGHT_ACCENT_BORDER : alpha(accent, 0.38)}`,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.35)}`,
    };
};

export const carouselPlayerVolumeLabelSx = (theme: Theme): SxProps<Theme> => ({
    color: isProfessionalLightTheme(theme)
        ? PROFESSIONAL_LIGHT_ACCENT
        : alpha(theme.palette.secondary.light, 0.95),
});

/** Folder icons in asset grid/table — PROFESSIONAL light only. */
export const assetExplorerFolderIconColor = (theme: Theme): string =>
    isProfessionalLightTheme(theme) ? PROFESSIONAL_LIGHT_ACCENT : 'secondary.main';

/** Upload dialog file-type placeholder icons (VIDEO, AUDIO, DOCUMENT, ZIP, etc.). */
export const assetExplorerUploadTypeIconColor = (theme: Theme): string =>
    isProfessionalLightTheme(theme) ? PROFESSIONAL_LIGHT_ACCENT : theme.palette.secondary.main;

export const assetExplorerUploadThumbnailBg = (theme: Theme): string =>
    isProfessionalLightTheme(theme)
        ? 'rgba(25, 25, 25, 0.08)'
        : alpha(theme.palette.secondary.main, 0.12);

/** Asset type chip (table view) — PROFESSIONAL light VIDEO badge. */
export const assetExplorerTypeChipSx = (theme: Theme, assetType: string): SxProps<Theme> => {
    const base = { fontWeight: 500, fontSize: '0.7rem' } as const;
    if (!isProfessionalLightTheme(theme) || assetType !== 'VIDEO') {
        return base;
    }
    return {
        ...base,
        fontWeight: 600,
        bgcolor: 'rgba(25, 25, 25, 0.1)',
        color: PROFESSIONAL_LIGHT_ACCENT,
        border: `1px solid ${PROFESSIONAL_LIGHT_ACCENT_BORDER}`,
        '& .MuiChip-label': { color: PROFESSIONAL_LIGHT_ACCENT },
    };
};

/** Folder tree row text/icon colors on PROFESSIONAL light. */
export const professionalLightFolderTreeColors = (theme: Theme, isSelected: boolean) => {
    if (!isProfessionalLightTheme(theme)) {
        return {
            iconColor: isSelected ? theme.palette.secondary.main : theme.palette.warning.main,
            textColor: isSelected ? theme.palette.secondary.main : theme.palette.text.primary,
            rowBg: isSelected ? undefined as string | undefined : undefined,
            rowHoverBg: undefined as string | undefined,
        };
    }
    return {
        iconColor: PROFESSIONAL_LIGHT_ACCENT,
        textColor: PROFESSIONAL_LIGHT_ACCENT,
        rowBg: isSelected ? 'rgba(25, 25, 25, 0.1)' : 'transparent',
        rowHoverBg: isSelected ? 'rgba(25, 25, 25, 0.14)' : 'rgba(25, 25, 25, 0.05)',
    };
};
